import React from 'react';
import { Tag } from 'antd';

/**
 * StatusBadge — 状態バッジ
 * ------------------------------------------------------------------
 * antd Tag を直接使う代わりの共通バッジ。色を「意味（tone）」で指定し、
 * 配色は全て tokens.css のテーマ色（background / active）に集約する。
 * これにより `color="green"` のような antd 既定色の散在を防ぎ、
 * デザインシステムの配色へ統一する（Ph.3）。
 *
 * 設計方針:
 *   - tone（意味）→ tokens.css の背景色 + テキスト色 のマッピングのみを持つ。
 *   - antd Tag を bordered={false} で薄くラップする。
 *   - 形状は 2 variant:
 *       soft（既定）… 薄い -background 背景 + 濃い -active 文字 + pill 形状（Ph.5）。
 *                      テキストは各テーマの -active（濃い段）で薄い背景上のコントラストを確保。
 *       solid        … 濃い -default 背景 + 白文字 + 角丸小（四角）。強い状態強調用。
 *                      （企画 デザイン修正依頼1 item 10.4「メール配信状況 = 四角枠・ソリッド・白文字」/ 2 次トラック S2）
 *
 * tone 一覧:
 *   success    … 完了・正常（green）
 *   warning    … 注意・期限警告（yellow）
 *   error      … エラー・無効・破壊的（red）
 *   info       … 情報・補助（blue）
 *   processing … 進行中・実施中（orange = primary テーマ）
 *   neutral    … 既定・未設定・コード表示（grey）
 *
 * 調査ステータス（surveyStatus.js）への推奨マッピング（2 次トラック S2 で Ph.6-1 から意味色を再変更）:
 *   0 実施前=error（赤） / 1 一時保存中=warning（黄） / 5 実施済み=success（緑） /
 *   7 期限切れ=neutral（灰） / 9 実施無効=neutral（灰）
 *   → SURVEY_STATUS_TONE を export しているので利用可能:
 *     <StatusBadge tone={SURVEY_STATUS_TONE[code]}>{surveyStatus[code]}</StatusBadge>
 *
 * @param {object}      props
 * @param {React.ReactNode} props.children            バッジ内のラベルテキスト（必須）
 * @param {'success'|'warning'|'error'|'info'|'processing'|'neutral'} [props.tone='neutral'] 意味カラー
 * @param {'soft'|'solid'} [props.variant='soft']     形状・配色（soft=薄 pill / solid=濃 四角・白文字）
 * @param {React.CSSProperties} [props.style]         追加スタイル（マージされる）
 *  ...rest                                           その他 antd Tag props を素通し（icon 等）
 *
 * @example
 * <StatusBadge tone="success">実施済み</StatusBadge>
 * <StatusBadge tone="error">非公開</StatusBadge>
 * <StatusBadge tone="warning" variant="solid">予約中</StatusBadge>
 */

// tone → tokens.css 配色（背景 / テキスト）。HEX は書かず CSS 変数で参照。
// soft（既定）= 薄い -background 背景 + 濃い -active 文字（pill）。
const TONE_STYLE = {
  success:    { background: 'var(--green-background)',  color: 'var(--green-active)' },
  warning:    { background: 'var(--yellow-background)', color: 'var(--yellow-active)' },
  error:      { background: 'var(--red-background)',    color: 'var(--red-active)' },
  info:       { background: 'var(--blue-background)',   color: 'var(--blue-active)' },
  processing: { background: 'var(--orange-background)', color: 'var(--orange-active)' },
  neutral:    { background: 'var(--grey-10)',           color: 'var(--grey-65)' },
};

// solid = 濃い -default 背景 + 白文字（四角）。状態を強く示す配信状況等で使用（2 次トラック S2）。
// ※ 白文字 on -default は WCAG AA（4.5:1）未達だが、企画ポリシー優先の「既知の AA 例外」
//   （item 10.4 が「白文字」を明示指定。S1 のブランドボタンと同じ扱い。design-system.md「アクセシビリティ」節参照）。
const SOLID_TONE_STYLE = {
  success:    { background: 'var(--green-default)',  color: 'var(--white)' },
  warning:    { background: 'var(--yellow-default)', color: 'var(--white)' },
  error:      { background: 'var(--red-default)',    color: 'var(--white)' },
  info:       { background: 'var(--blue-default)',   color: 'var(--white)' },
  processing: { background: 'var(--orange-default)', color: 'var(--white)' },
  neutral:    { background: 'var(--grey-45)',        color: 'var(--white)' },
};

// 調査ステータスコード → tone の推奨マッピング（domain-codes.md / surveyStatus.js と対応）。
// ★2 次トラック S2（企画 デザイン修正依頼1 item 11）で Ph.6-1 の意味色を政策優先で再変更:
//   実施前 中立灰→エラー赤 / 一時保存中 橙→警告黄 / 期限切れ 警告黄→中立灰 / 無効 エラー赤→中立灰。
export const SURVEY_STATUS_TONE = {
  0: 'error',     // 実施前（赤・要対応）
  1: 'warning',   // 一時保存中（黄・実施途中）
  5: 'success',   // 実施済み（緑）
  7: 'neutral',   // 期限切れ（灰）
  9: 'neutral',   // 実施無効（灰）
};

const StatusBadge = ({ children, tone = 'neutral', variant = 'soft', style, ...rest }) => {
  const isSolid = variant === 'solid';
  const palette = isSolid ? SOLID_TONE_STYLE : TONE_STYLE;
  const toneStyle = palette[tone] || palette.neutral;

  const mergedStyle = {
    ...toneStyle,
    border: 'none',
    // 形状: soft = 丸み最大の pill（Ph.5 / 計画書 #6）、solid = 四角（--radius-sm）で pill と明確に区別。
    borderRadius: isSolid ? 'var(--radius-sm)' : 'var(--radius-pill)',
    paddingInline: 'var(--space-3)',
    fontWeight: 'var(--font-medium)',
    ...style,
  };

  return (
    <Tag bordered={false} style={mergedStyle} {...rest}>
      {children}
    </Tag>
  );
};

export default StatusBadge;
